import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { createPortal } from "react-dom";
import { fetchLabels } from "../../../utils/api";
import { mapChromeShell } from "../../../styles/mapChromeShell";

const sortLabels = (data) => {
    const noneLabel = data.find((label) => label.name === "ラベルなし");
    const restLabels = data.filter((label) => label.name !== "ラベルなし");
    return noneLabel ? [noneLabel, ...restLabels] : restLabels;
};

/**
 * externalLabels の意味:
 *   null      = 親がまだロード中 → 自前フェッチもせず loading=true を返す
 *   []        = 親がロード済みだがラベルなし → そのまま使用
 *   配列      = 親がロード済み → そのまま使用
 *   未指定    = 親が関与しない（スタンドアロン用途）→ 自前フェッチ
 */
const useLabelData = (projectId, externalLabels = undefined, publicUrl = null) => {
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (externalLabels === null) {
            setLoading(true);
            return;
        }

        if (Array.isArray(externalLabels)) {
            setLabels(sortLabels(externalLabels));
            setLoading(false);
            return;
        }

        if (publicUrl) {
            setLabels([]);
            setLoading(false);
            return;
        }

        const loadLabels = async () => {
            if (!projectId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const data = await fetchLabels(projectId);
                setLabels(sortLabels(data));
            } catch (err) {
                console.error("Failed to fetch labels:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadLabels();
    }, [projectId, externalLabels, publicUrl]);

    return { labels, loading, error };
};

const useSelectionState = (labels, initialValue, onChange) => {
    const allSelected = useMemo(
        () => labels.length > 0 && initialValue.length === labels.length,
        [labels.length, initialValue.length]
    );

    const toggleAll = useCallback(() => {
        onChange(allSelected ? [] : labels.map((label) => label.id));
    }, [allSelected, labels, onChange]);

    const toggleOne = useCallback(
        (id) => {
            onChange(
                initialValue.includes(id)
                    ? initialValue.filter((value) => value !== id)
                    : [...initialValue, id]
            );
        },
        [initialValue, onChange]
    );

    return { allSelected, toggleAll, toggleOne };
};

function getFilterBadge(allSelected, selectedCount, hasLabels) {
    // 全部表示は「全て」。部分選択は件数。「—」は0件選択（何も出ない）。
    if (!hasLabels) return null;
    if (allSelected) {
        return { label: "全て", tone: "neutral" };
    }
    if (selectedCount === 0) {
        return { label: "—", tone: "active" };
    }
    return { label: String(selectedCount), tone: "active" };
}

function FilterControl({ isOpen, onToggle, selectedCount, allSelected, hasLabels, children }) {
    const isFiltered = !allSelected;
    const badge = getFilterBadge(allSelected, selectedCount, hasLabels);
    const badgeToneClass = !badge
        ? mapChromeShell.filterTriggerBadgeIdle
        : badge.tone === "neutral"
          ? mapChromeShell.filterTriggerBadgeNeutral
          : mapChromeShell.filterTriggerBadgeActive;

    return (
        <div className={mapChromeShell.filterControlContainer}>
            <button
                type="button"
                className={`${mapChromeShell.filterTrigger} ${isOpen || isFiltered ? mapChromeShell.filterTriggerOpen : ""}`}
                onClick={onToggle}
                aria-expanded={isOpen}
            >
                <span className={mapChromeShell.filterTriggerIcon}>filter_list</span>
                <span className={mapChromeShell.filterTriggerLabel}>フィルター</span>
                <span
                    className={`${mapChromeShell.filterTriggerBadge} ${badgeToneClass}`}
                    aria-hidden={!badge}
                >
                    {badge?.label ?? ""}
                </span>
            </button>
            {isOpen && (
                <div className={mapChromeShell.filterPanel}>
                    {children}
                </div>
            )}
        </div>
    );
}

function FilterHeader({ onClose }) {
    return (
        <div className={mapChromeShell.filterPanelHeader}>
            <h3 className={mapChromeShell.filterPanelTitle}>
                ラベルフィルター
            </h3>
            <button
                type="button"
                className={mapChromeShell.filterPanelClose}
                aria-label="メニューを閉じる"
                onClick={onClose}
            >
                <span className={mapChromeShell.filterPanelCloseIcon}>close</span>
            </button>
        </div>
    );
}

function FilterItem({ checked, onChange, colorStyle, children }) {
    return (
        <label className={mapChromeShell.filterPanelItem}>
            <input
                type="checkbox"
                className={mapChromeShell.filterPanelCheckbox}
                checked={checked}
                onChange={onChange}
            />
            <span
                className={mapChromeShell.filterPanelSwatch}
                style={colorStyle}
            />
            <span className={mapChromeShell.filterPanelItemLabel}>
                {children}
            </span>
        </label>
    );
}

function FilterContent({ labels, value, allSelected, toggleAll, toggleOne, loading, error }) {
    if (loading) {
        return <p className={mapChromeShell.filterPanelStatus}>ラベルを読み込み中...</p>;
    }

    if (error) {
        return <p className={mapChromeShell.filterPanelError}>ラベルの読み込みに失敗しました</p>;
    }

    if (labels.length === 0) {
        return <p className={mapChromeShell.filterPanelStatus}>ラベルがありません</p>;
    }

    return (
        <div className={mapChromeShell.filterPanelList}>
            <FilterItem
                checked={allSelected}
                onChange={toggleAll}
                colorStyle={{ background: "var(--label-all-swatch)", borderColor: "rgba(0,0,0,0.2)" }}
            >
                全て
            </FilterItem>

            {labels.map((label) => (
                <FilterItem
                    key={label.id}
                    checked={value.includes(label.id)}
                    onChange={() => toggleOne(label.id)}
                    colorStyle={{ background: label.color }}
                >
                    {label.name}
                </FilterItem>
            ))}
        </div>
    );
}

function LabelFilterMapControl({ projectId, value, onChange, labels: externalLabels, publicUrl }) {
    const map = useMap();
    const { labels, loading, error } = useLabelData(projectId, externalLabels, publicUrl);
    const { allSelected, toggleAll, toggleOne } = useSelectionState(labels, value, onChange);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCount, setSelectedCount] = useState(0);
    const controlRef = useRef(null);
    const isInitialized = useRef(false);

    useEffect(() => {
        setSelectedCount(value.length);
    }, [value]);

    useEffect(() => {
        if (!map || !window.google || isInitialized.current) return;

        const controlDiv = document.createElement("div");
        controlDiv.className = mapChromeShell.layerToggleContainer;
        controlRef.current = controlDiv;

        map.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);
        isInitialized.current = true;

        return () => {
            const index = map.controls[window.google.maps.ControlPosition.TOP_RIGHT]
                .getArray()
                .indexOf(controlDiv);
            if (index > -1) {
                map.controls[window.google.maps.ControlPosition.TOP_RIGHT].removeAt(index);
            }
            isInitialized.current = false;
        };
    }, [map]);

    const handleToggle = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    if (!controlRef.current) {
        return null;
    }

    return createPortal(
        <FilterControl
            isOpen={isOpen}
            onToggle={handleToggle}
            selectedCount={selectedCount}
            allSelected={allSelected}
            hasLabels={labels.length > 0}
        >
            <FilterHeader onClose={handleClose} />
            <FilterContent
                labels={labels}
                value={value}
                allSelected={allSelected}
                toggleAll={toggleAll}
                toggleOne={toggleOne}
                loading={loading}
                error={error}
            />
        </FilterControl>,
        controlRef.current
    );
}

export default LabelFilterMapControl;
